import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSharePointAuth } from '@/hooks/useSharePointAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Cloud, Loader2, Unlink, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

export function SharePointAuthButton({ onConnected }: { onConnected?: () => void }) {
  const { isConnected, loading, tokenExpiresAt: tokenExpiry, initiateAuth, disconnect, checkConnection } = useSharePointAuth();
  const [refreshing, setRefreshing] = useState(false);
  const { toast } = useToast();

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { error } = await supabase.functions.invoke('sharepoint-refresh-token');
      if (error) throw error;
      toast({ title: 'Token ververst', description: 'Je SharePoint verbinding is vernieuwd' });
      // Explicit refresh: re-read the new expiry once.
      await checkConnection();
    } catch (error) {
      toast({ title: 'Refresh mislukt', description: 'Probeer opnieuw in te loggen', variant: 'destructive' });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (isConnected) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5 text-green-500" />
            SharePoint Verbonden
          </CardTitle>
          <CardDescription>
            Je SharePoint account is succesvol gekoppeld
            {tokenExpiry && (
              <span className="block text-xs mt-1">
                Token verloopt: {format(tokenExpiry, 'dd MMM yyyy HH:mm', { locale: nl })}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex-1"
          >
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Vernieuw
          </Button>
          <Button
            variant="outline"
            onClick={disconnect}
            className="flex-1"
          >
            <Unlink className="h-4 w-4 mr-2" />
            Ontkoppel
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>SharePoint Koppelen</CardTitle>
        <CardDescription>
          Koppel je Microsoft SharePoint account om documenten te synchroniseren
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Door je SharePoint account te koppelen krijg je toegang tot:
        </p>
        <ul className="text-sm space-y-2 text-muted-foreground">
          <li>• Automatische documentsynchronisatie</li>
          <li>• Bestandsbeheer direct vanuit het CRM</li>
          <li>• Preview van Word, Excel en PDF bestanden</li>
          <li>• Upload en download functionaliteit</li>
        </ul>
        <Button
          onClick={() => {
            initiateAuth();
            if (onConnected) onConnected();
          }}
          className="w-full"
        >
          <Cloud className="h-4 w-4 mr-2" />
          Verbind met SharePoint
        </Button>
      </CardContent>
    </Card>
  );
}
